import { logger } from '../utils/logger';

const API_BASE = '/.netlify/functions';

export interface TrialStatus {
  hasAccess: boolean;
  accessType: 'none' | 'trial' | 'premium' | 'expired';
  trialEligible: boolean;
  trialUsed: boolean;
  isTrialActive: boolean;
  isTrialExpired: boolean;
  trialStartedAt?: string;
  trialExpiresAt?: string;
  daysRemaining: number;
  lastCheck: string;
  isPremiumUser?: boolean;
  subscription?: {
    platform: string;
    expiresAt?: string;
  };
}

export interface TrialStartResponse {
  success: boolean;
  trialStartedAt: string;
  trialExpiresAt: string;
  daysRemaining: number;
  message: string;
}

// Cache keys
const CACHE_KEYS = {
  TRIAL_STATUS: 'trial-status-cache',
  LAST_SYNC: 'trial-last-sync',
  OFFLINE_GRACE: 'trial-offline-grace'
};

// Offline grace period: 7 days (prayer app users need offline access for retreats, travel, etc.)
const OFFLINE_GRACE_PERIOD = 7 * 24 * 60 * 60 * 1000;

/**
 * Check trial status from server
 * Falls back to cached status if offline (with 24-hour grace period)
 */
export const checkTrialStatus = async (
  email?: string,
  googleId?: string
): Promise<TrialStatus> => {
  try {
    // Build query params
    const params = new URLSearchParams();
    if (email) params.append('email', email);
    if (googleId) params.append('googleId', googleId);

    // Try to fetch from server
    const response = await fetch(`${API_BASE}/trial-status?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const status: TrialStatus = await response.json();

    // Cache successful response
    localStorage.setItem(CACHE_KEYS.TRIAL_STATUS, JSON.stringify(status));
    localStorage.setItem(CACHE_KEYS.LAST_SYNC, new Date().toISOString());

    logger.subscription.debug('Trial status fetched from server', status);
    return status;

  } catch (error) {
    logger.warn('Failed to fetch trial status from server, using cache', error);

    // Use cached status with offline grace period
    return getCachedTrialStatus();
  }
};

/**
 * Get cached trial status with offline grace period
 * Returns trial status if within 24-hour grace period, otherwise denies access
 */
const getCachedTrialStatus = (): TrialStatus => {
  const cached = localStorage.getItem(CACHE_KEYS.TRIAL_STATUS);
  const lastSync = localStorage.getItem(CACHE_KEYS.LAST_SYNC);

  if (!cached) {
    // No cache - deny access
    logger.warn('No cached trial status, denying access');
    return {
      hasAccess: false,
      accessType: 'none',
      trialEligible: true,
      trialUsed: false,
      isTrialActive: false,
      isTrialExpired: false,
      daysRemaining: 0,
      lastCheck: new Date().toISOString()
    };
  }

  const status: TrialStatus = JSON.parse(cached);
  const lastSyncTime = lastSync ? new Date(lastSync).getTime() : 0;
  const now = Date.now();
  const timeSinceSync = now - lastSyncTime;

  // If within grace period, use cached status
  if (timeSinceSync < OFFLINE_GRACE_PERIOD) {
    logger.debug('Using cached trial status (within grace period)', {
      timeSinceSync: Math.round(timeSinceSync / 1000 / 60) + ' minutes',
      graceRemaining: Math.round((OFFLINE_GRACE_PERIOD - timeSinceSync) / 1000 / 60) + ' minutes'
    });
    return status;
  }

  // Grace period expired - deny access until they go online
  logger.warn('Offline grace period expired, denying access');
  return {
    ...status,
    hasAccess: false,
    accessType: 'expired'
  };
};

/**
 * Start trial for user
 */
export const startTrial = async (
  email?: string,
  googleId?: string,
  deviceId?: string
): Promise<TrialStartResponse> => {
  try {
    const response = await fetch(`${API_BASE}/start-trial`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, googleId, deviceId })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to start trial');
    }

    const result: TrialStartResponse = await response.json();

    logger.subscription.trialStarted();

    // Immediately refresh trial status to update cache
    await checkTrialStatus(email, googleId);

    return result;

  } catch (error) {
    logger.error('Failed to start trial', error);
    throw error;
  }
};

/**
 * Check if user is within offline grace period
 */
export const isWithinGracePeriod = (): boolean => {
  const lastSync = localStorage.getItem(CACHE_KEYS.LAST_SYNC);
  if (!lastSync) return false;

  const lastSyncTime = new Date(lastSync).getTime();
  const now = Date.now();
  const timeSinceSync = now - lastSyncTime;

  return timeSinceSync < OFFLINE_GRACE_PERIOD;
};

/**
 * Get offline grace period info
 */
export const getGracePeriodInfo = (): {
  withinGracePeriod: boolean;
  minutesSinceSync: number;
  minutesRemaining: number;
  hoursSinceSync: number;
  hoursRemaining: number;
} => {
  const lastSync = localStorage.getItem(CACHE_KEYS.LAST_SYNC);
  if (!lastSync) {
    return {
      withinGracePeriod: false,
      minutesSinceSync: 0,
      minutesRemaining: 0,
      hoursSinceSync: 0,
      hoursRemaining: 0
    };
  }

  const lastSyncTime = new Date(lastSync).getTime();
  const now = Date.now();
  const timeSinceSync = now - lastSyncTime;
  const timeRemaining = Math.max(0, OFFLINE_GRACE_PERIOD - timeSinceSync);

  return {
    withinGracePeriod: timeSinceSync < OFFLINE_GRACE_PERIOD,
    minutesSinceSync: Math.round(timeSinceSync / 1000 / 60),
    minutesRemaining: Math.round(timeRemaining / 1000 / 60),
    hoursSinceSync: Math.round(timeSinceSync / 1000 / 60 / 60),
    hoursRemaining: Math.round(timeRemaining / 1000 / 60 / 60)
  };
};

/**
 * Clear trial cache (for testing/debugging)
 */
export const clearTrialCache = (): void => {
  localStorage.removeItem(CACHE_KEYS.TRIAL_STATUS);
  localStorage.removeItem(CACHE_KEYS.LAST_SYNC);
  logger.debug('Trial cache cleared');
};

/**
 * Force refresh trial status (useful after auth changes)
 */
export const forceRefreshTrialStatus = async (
  email?: string,
  googleId?: string
): Promise<TrialStatus> => {
  logger.debug('Force refreshing trial status');
  // Clear cache first to ensure fresh fetch
  clearTrialCache();
  return checkTrialStatus(email, googleId);
};
